/**
 * Tiny sound-effect synth built on the Web Audio API — no audio files to ship or preload.
 * The AudioContext is normally built by `warm()`, called once at idle time (see main.tsx), so
 * the ~180ms first-construction cost lands before any tap rather than on one. It starts
 * 'suspended' outside a user gesture, which Chrome and Safari both allow; `audio.unlock()`
 * still calls `unlock()` on the first real tap to resume() it, so playback rules are unchanged.
 * If warm() never ran (tests, a gesture that beats idle), ensure() still builds it lazily.
 * Every public method swallows its own errors: sound is a nice-to-have and must never throw
 * or block the caller (headless test browsers routinely start with a suspended/absent context).
 */
class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const Ctor =
        typeof window === 'undefined'
          ? undefined
          : (window.AudioContext ??
            (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
      if (!Ctor) return null;
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = 0.35;
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;
      return ctx;
    } catch {
      return null;
    }
  }

  /** Build the AudioContext ahead of any tap, from idle time (see main.tsx). Deliberately does
   * not resume() it: outside a gesture that would be ignored anyway, and a fresh context starts
   * suspended, which is fine to leave for unlock()/ready() to pick up inside the real gesture. */
  warm(): void {
    try {
      this.ensure();
    } catch {
      /* never block idle-time warm-up */
    }
  }

  /** Call from a user-gesture handler (audio.unlock() does this) so playback is allowed later. */
  unlock(): void {
    try {
      const ctx = this.ensure();
      if (ctx?.state === 'suspended') void ctx.resume().catch(() => {});
    } catch {
      /* never block a tap on audio setup */
    }
  }

  /** One tone: quick attack, exponential decay so it never clicks or overstays. */
  private tone(
    freq: number,
    startOffset: number,
    dur: number,
    opts: { type?: OscillatorType; gain?: number } = {},
  ): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const osc = ctx.createOscillator();
    osc.type = opts.type ?? 'sine';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    const peak = opts.gain ?? 1;
    const t0 = ctx.currentTime + startOffset;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.linearRampToValueAtTime(peak, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(gain);
    gain.connect(master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private ready(): AudioContext | null {
    const ctx = this.ensure();
    if (ctx?.state === 'suspended') void ctx.resume().catch(() => {});
    return ctx;
  }

  /** Bright two-note chime: E5 -> G5, ~0.25s. */
  correct(): void {
    try {
      if (!this.ready()) return;
      this.tone(659.25, 0, 0.14, { type: 'sine', gain: 0.9 });
      this.tone(659.25, 0, 0.14, { type: 'triangle', gain: 0.25 });
      this.tone(783.99, 0.1, 0.16, { type: 'sine', gain: 0.9 });
      this.tone(783.99, 0.1, 0.16, { type: 'triangle', gain: 0.25 });
    } catch {
      /* never throw from a sound effect */
    }
  }

  /** Gentle low "bonk", ~0.2s — never harsh. */
  wrong(): void {
    try {
      if (!this.ready()) return;
      this.tone(220, 0, 0.2, { type: 'sine', gain: 0.5 });
    } catch {
      /* never throw from a sound effect */
    }
  }

  /** Tiny click for a UI tap. */
  tap(): void {
    try {
      if (!this.ready()) return;
      this.tone(900, 0, 0.045, { type: 'square', gain: 0.12 });
    } catch {
      /* never throw from a sound effect */
    }
  }

  /** Four-note rising arpeggio with a little shimmer, ~0.9s. */
  fanfare(): void {
    try {
      if (!this.ready()) return;
      const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
      notes.forEach((freq, i) => {
        const start = i * 0.18;
        this.tone(freq, start, 0.35, { type: 'sine', gain: 0.8 });
        this.tone(freq * 2, start, 0.22, { type: 'triangle', gain: 0.1 });
      });
    } catch {
      /* never throw from a sound effect */
    }
  }
}

export const sfx = new Sfx();
